<template>
  <div class="content-selector-assets flex flex-col h-full">
    <NGrid class="h-full overflow-hidden mt-3" cols="12" item-responsive>
      <NGridItem ref="scrollableContainer" class="h-full overflow-auto" span="12 650:7 1200:8 1400:9">
        <CFullScreenLoader v-if="isFetchingAssets" fit-parent />
        <template v-else>
          <div class="sticky top-0 z-1 bg-primary6 pb-2">
            <CWarningAlert
              class="mb-2 mr-2"
              persistent
              :title="t('canvasUI.contentSelector.syncIosWarning')"
              :visible="isPitcherOrIosDevice"
            />
            <div class="flex flex-col items-center justify-center">
              <AssetUploader v-if="!launchDarkly.hide_asset_uploader" @uploads-complete="() => fetchAssets(true)" />
              <NPagination
                v-if="paginatedSortedAssets?.length && !launchDarkly.enable_search_and_filters_in_multimedia_component"
                v-model:page="activePage"
                :page-count="paginatedSortedAssets.length"
                :page-size="pageSize"
              />
            </div>
            <div v-if="!!launchDarkly.enable_search_and_filters_in_multimedia_component" class="my-2">
              <div class="flex flex-wrap gap-3 items-start">
                <CSearch
                  v-model="search"
                  class="min-w-[280px] flex-1 max-w-[480px]"
                  :placeholder="t('canvasUI.canvasBuilder.searchPlaceholder')"
                  size="large"
                  transparent
                />

                <div class="flex flex-wrap gap-2 items-center flex-shrink-0">
                  <Component
                    :is="componentMap[getComponentNameByFilterType(f.type)]"
                    v-for="f in dataFilterComponents"
                    :key="f.name"
                    :allow-custom-date-range="f.type === 'date'"
                    class="flex-shrink-0"
                    :filter-key="f.name"
                    :modelValue="filters[f.name]"
                    :multiple="f.type === 'multi-select' ? true : undefined"
                    :name="f.label || f.name"
                    :options="getFilterOptions(f.type, f.options)"
                    @update:modelValue="updateFilters(f, $event)"
                  />
                  <CButton
                    v-if="hasActiveFilters"
                    class="pa-1 pl-2 !normal-case !tracking-normal underline flex-shrink-0"
                    size="tiny"
                    :style="{ borderLeft: `1px solid ${themeVars.primary5}` }"
                    text
                    @click="clearFilters"
                  >
                    {{ t('canvasUI.contentSelector.clearFilters') }}
                  </CButton>
                  <CSelectFilter
                    class="flex-shrink-0"
                    :filter-key="'sortBy'"
                    :modelValue="sortBy"
                    :name="t('canvasUI.contentSelector.sortBy')"
                    :options="sortOptions"
                    @update:modelValue="sortBy = $event as 'alphabetical' | 'owner' | 'modifiedAt' | 'createdAt'"
                  />
                  <NPagination
                    v-if="paginatedSortedAssets?.length && !(hasActiveFilters || search)"
                    v-model:page="activePage"
                    class="flex-shrink-0"
                    :page-count="paginatedSortedAssets.length"
                    :page-size="pageSize"
                  />
                </div>
              </div>
            </div>
          </div>

          <NFlex v-if="filteredAssets?.length" wrap>
            <GridItem
              v-for="asset in filteredAssets"
              :key="asset.id"
              :delete-disabled-tooltip="t('canvasUI.contentSelector.deleteAssetWarning.themeAssetTooltip')"
              :is-delete-disabled="themeAssetIds.includes(asset.id)"
              :is-selected="selectedAssets.some((a) => a.id === asset.id)"
              :item="{
                file: { id: asset.id, name: asset.name },
                name: asset.name,
                thumbnail_url: getThumbnailUrl(asset),
                id: asset.id,
                content_extension: asset.content_extension,
                content_url: asset.content_url,
                type: 'asset',
                created_by_user_id: asset.created_by?.id,
              }"
              @item-deleted="onDeleteFile"
              @item-deselected="deselectAsset"
              @item-selected="selectItem"
            />
          </NFlex>
        </template>
      </NGridItem>
      <NGridItem class="h-full overflow-auto" span="12 650:5 1200:4 1400:3">
        <SelectionPanel />
      </NGridItem>
    </NGrid>
  </div>
</template>

<script setup lang="ts">
import type { AxiosInstance } from 'axios'
import { NGridItem, NGrid, NPagination, NFlex } from 'naive-ui'
import { chunk, omitBy } from 'lodash-es'
import { inject, onMounted, ref, computed, type ComputedRef, type ToRefs, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocalStorage } from '@vueuse/core'

import AssetUploader from '@content-selector/components/Assets/AssetUploader.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CDateRangeFilter from '@lib/components/filters/CDateRangeFilter.vue'
import CFullScreenLoader from '@lib/components/CFullScreenLoader/CFullScreenLoader.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import CSelectFilter from '@lib/components/filters/CSelectFilter.vue'
import GridItem from '@content-selector/components/ui/GridItem.vue'
import SelectionPanel from '@content-selector/components/Content/SelectionPanel/SelectionPanel.vue'

import type { SelectedAsset } from '@content-selector/stores/app'
import { useStoreApi, useStore } from '@content-selector/composables/useStore'
import useConfirmation from '@lib/composables/useConfirmation'
import useMetadataFilters from '@lib/composables/useMetadataFilters'
import useThemeVars from '@lib/composables/useThemeVars'

import type { AppProps } from '@content-selector/appTypes'
import { MetadataTemplateFieldTypeEnum, type File } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import CWarningAlert from '@lib/components/CWarningAlert/CWarningAlert.vue'
import { isPitcherOrIosDevice } from '@lib/util/userAgents'
import useAppEmit from '@lib/composables/appEmit.use'

const appProps = inject<ToRefs<AppProps>>('appProps')
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const coreRestApiAxios = inject<Ref<AxiosInstance | undefined>>('coreRestApiAxios')
const pitcherInfo = inject<ComputedRef<{ instanceId?: string }>>(
  'pitcherInfo',
  computed(() => ({})),
)

const { appEmit } = useAppEmit()
const { t } = useI18n()
const { getComponentNameByFilterType, getFilterOptions } = useMetadataFilters(t)
const { getThumbnailUrl, selectItem, deselectAsset, selectedAssets } = useStore()
const { state: api } = useStoreApi()
const confirmation = useConfirmation()
const themeVars = useThemeVars()

const assets = ref<File[]>([])
const activePage = ref(1)
const isFetchingAssets = ref(false)
const search = ref('')
const filters = ref<Record<string, any>>({})
const themeAssetIds = ref<string[]>([])
const scrollableContainer = ref<InstanceType<typeof NGridItem>>()

const storeSortingPreference = computed(() => launchDarkly.value.enable_persistent_assets_sorting)

const sortByLocalStorage = useLocalStorage<'alphabetical' | 'owner' | 'modifiedAt' | 'createdAt'>(
  'asset-selector-sort-preference',
  'alphabetical',
  {
    listenToStorageChanges: true,
  },
)
const sortByRef = ref<'alphabetical' | 'owner' | 'modifiedAt' | 'createdAt'>('alphabetical')

const sortBy = computed({
  get: () => (storeSortingPreference.value ? sortByLocalStorage.value : sortByRef.value),
  set: (value: 'alphabetical' | 'owner' | 'modifiedAt' | 'createdAt') => {
    if (storeSortingPreference.value) {
      sortByLocalStorage.value = value
    } else {
      sortByRef.value = value
    }
  },
})

const componentMap = {
  CSelectFilter,
  CDateRangeFilter,
} as Record<string, any>

const sortedAssets = computed(() => {
  const assetsCopy = [...assets.value]

  switch (sortBy.value) {
    case 'alphabetical':
      return assetsCopy.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    case 'owner':
      return assetsCopy.sort((a, b) => {
        const ownerA = a.created_by?.name || ''
        const ownerB = b.created_by?.name || ''
        return ownerA.localeCompare(ownerB)
      })
    case 'modifiedAt':
      return assetsCopy.sort((a, b) => {
        const dateA = new Date(a.modified_at || a.created_at).getTime()
        const dateB = new Date(b.modified_at || b.created_at).getTime()
        return dateB - dateA // Most recent first
      })
    case 'createdAt':
      return assetsCopy.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return dateB - dateA // Most recent first
      })
    default:
      return assetsCopy
  }
})

const paginatedSortedAssets = computed(() => chunk(sortedAssets.value, pageSize))
const activeChunk = computed(() => paginatedSortedAssets.value[activePage.value - 1])

const filteredAssets = computed(() => {
  let filtered = sortedAssets.value || []

  if (filters.value.content_extension?.length) {
    filtered = filtered.filter((a) => filters.value.content_extension.includes(a.content_extension?.toLowerCase()))
  }

  if (filters.value.created_by?.length) {
    filtered = filtered.filter((a) => filters.value.created_by.includes(a.created_by?.id?.toString()))
  }

  if (hasActiveFilters.value || search.value) {
    if (filters.value.created_at) {
      const [from, to] = filters.value.created_at
      filtered = filtered.filter((a) => {
        const createdAt = new Date(a.created_at).getTime()

        const fromDate = new Date(from)
        fromDate.setHours(0, 0, 0, 0)

        const toDate = new Date(to)
        toDate.setHours(23, 59, 59, 999)

        return createdAt >= fromDate.getTime() && createdAt <= toDate.getTime()
      })
    }

    if (search.value) {
      const searchLower = search.value.toLowerCase()
      filtered = filtered.filter((a) => a?.name?.toLowerCase().includes(searchLower))
    }

    return filtered
  }

  return activeChunk.value
})

const dataFilterComponents = computed(() => [
  {
    type: MetadataTemplateFieldTypeEnum.MULTI_SELECT,
    name: 'content_extension',
    label: t('canvasUI.contentSelector.filters.type'),
    options: distinctExtensions.value.map((ext) => ({
      label: ext as string,
      value: ext as string,
    })),
  },
  {
    type: MetadataTemplateFieldTypeEnum.MULTI_SELECT,
    name: 'created_by',
    label: t('canvasUI.contentSelector.filters.owner'),
    options: distinctOwners.value.map((owner) => ({
      label: owner.name,
      value: owner.id,
    })),
  },
  {
    type: MetadataTemplateFieldTypeEnum.DATE,
    name: 'created_at',
    label: t('canvasUI.contentSelector.filters.uploadedAt'),
  },
])
const distinctExtensions = computed(() => {
  const extensions = new Set(assets.value?.map((asset) => asset.content_extension?.toLowerCase()) ?? [])
  return Array.from(extensions).filter(Boolean).sort()
})
const distinctOwners = computed(() => {
  const owners = new Map<string, { id: string; name: string }>()
  assets.value?.forEach((asset) => {
    if (asset.created_by?.id && asset.created_by?.name) {
      owners.set(asset.created_by.id.toString(), {
        id: asset.created_by.id.toString(),
        name: asset.created_by.name,
      })
    }
  })
  return Array.from(owners.values()).sort((a, b) => a.name.localeCompare(b.name))
})
const sortOptions = computed(() => [
  { label: t('canvasUI.contentSelector.sort.alphabetical'), value: 'alphabetical' },
  { label: t('canvasUI.contentSelector.sort.owner'), value: 'owner' },
  { label: t('canvasUI.contentSelector.sort.modifiedAt'), value: 'modifiedAt' },
  { label: t('canvasUI.contentSelector.sort.createdAt'), value: 'createdAt' },
])
const hasActiveFilters = computed(() =>
  Object.values(filters.value).some((v) => {
    if (Array.isArray(v)) {
      return v.length > 0
    }

    return !!v
  }),
)
const pageSize = 100

function clearFilters() {
  filters.value = {}
}

async function fetchAssets(afterUpload = false) {
  if (api.fileListFetcher) {
    try {
      isFetchingAssets.value = true
      const filtersToFetch = { type: ['asset'] } as Record<string, any>
      appProps?.allowedAssetContentTypes?.value &&
        (filtersToFetch.content_type = appProps.allowedAssetContentTypes.value)
      const r = await api.fileListFetcher({ filters: filtersToFetch, page_size: 100 })
      // Filter viewable assets (sorting is handled by sortedAssets computed)
      assets.value = r.filter((a) => a.status === 'viewable')
      if (afterUpload) {
        sortBy.value = 'createdAt'
      }
    } catch (e) {
      console.error(e)
    } finally {
      isFetchingAssets.value = false
      activePage.value = 1
    }
  }
}

async function fetchThemeAssetIds() {
  if (launchDarkly.value.enable_themes && launchDarkly.value.enable_theme_assets && pitcherInfo.value?.instanceId) {
    try {
      if (!coreRestApiAxios?.value) {
        console.error('coreRestApiAxios is not defined')
        return
      }
      const result = await coreRestApiAxios.value.get<unknown, { asset_ids: string[] }>('protected/themes/asset-ids', {
        params: {
          instance_id: pitcherInfo.value.instanceId,
        },
      })
      themeAssetIds.value = result.asset_ids
    } catch (e) {
      console.error('Failed to fetch theme asset ids:', e)
      themeAssetIds.value = []
    }
  }
}

async function onDeleteFile(item: { id: File['id'] }) {
  confirmation.danger({
    title: t('canvasUI.contentSelector.deleteAssetConfirmation.title'),
    content: t('canvasUI.contentSelector.deleteAssetConfirmation.content'),
    confirmText: t('canvasUI.contentSelector.deleteAssetConfirmation.confirm'),
    cancelText: t('canvasUI.contentSelector.deleteAssetConfirmation.cancel'),
    hasExit: false,
    zIndex: 10000, // Ensure the confirmation dialog appears on top
    onConfirm: async () => {
      try {
        if (!api.fileDeleter) {
          console.warn('no deleter provided')
          return
        }
        await api.fileDeleter(item.id)

        deselectAsset(item as SelectedAsset)
        assets.value = assets.value.filter((a) => a.id !== item.id)
        activePage.value = 1
        appEmit('asset-deleted', item.id)
      } catch (error) {
        console.error(error)
        return
      }
    },
  })
}

function updateFilters(f: Record<string, any>, value: string) {
  filters.value = omitBy({ ...filters.value, [f.name]: value }, (v) => v === '' || v === null)
}

onMounted(async () => {
  fetchAssets()
  fetchThemeAssetIds()
})
</script>
